import { motion } from "framer-motion";
import PageHeader from "@/components/site/PageHeader";
import { useSEO } from "@/lib/seo";
import { FIRM } from "@/lib/content";

const SECTIONS = [
    {
        title: "Allmänt",
        body: `Dessa allmänna villkor gäller för uppdrag som utförs av ${FIRM.name} (enskild firma, org.nr ${FIRM.orgNr}) för klients räkning. Villkoren utgör tillsammans med det specifika uppdragsbrevet avtalet mellan byrån och klienten. Avvikande villkor gäller endast om de skriftligen avtalats.`,
    },
    {
        title: "Uppdragets omfattning",
        body: `Uppdraget definieras genom skriftlig eller muntlig överenskommelse mellan byrån och klienten. Byrån företräder klienten endast i de frågor som uttryckligen omfattas av uppdraget. Rådgivning i ett ärende får inte åberopas i annat sammanhang utan byråns skriftliga medgivande.`,
    },
    {
        title: "Arvode och kostnader",
        body: `Arvode debiteras enligt byråns vid var tid gällande timarvode om inte annat skriftligen överenskommits. Utöver arvodet tillkommer utlägg (t.ex. ansökningsavgifter, rese- och kopieringskostnader) samt lagstadgad mervärdesskatt.

Fakturering sker månadsvis, eller vid uppdragets avslutning, med betalningsvillkor 20 dagar netto. Vid dröjsmål tillkommer dröjsmålsränta enligt räntelagen.`,
    },
    {
        title: "Rättsskydd och rättshjälp",
        body: `När det är möjligt ansöker byrån å klientens vägnar om ersättning via klientens försäkring (rättsskydd) eller om statlig rättshjälp. Beviljas ersättning regleras merparten av kostnaderna av försäkringsbolaget/staten. Klienten ansvarar alltid för självrisk respektive rättshjälpsavgift samt för kostnader som inte täcks.`,
    },
    {
        title: "Förskott och klientmedel",
        body: `Byrån kan begära förskott innan arbetet påbörjas eller under uppdragets gång. Medel som hör till klienten sätts in på separat klientmedelskonto och behandlas i enlighet med god sed.`,
    },
    {
        title: "Sekretess och intressekonflikter",
        body: `Byrån omfattas av sekretess. Information om klient och uppdrag lämnas inte ut utan klientens samtycke, förutom när detta krävs enligt lag.

Innan ett uppdrag antas genomförs en intressekonfliktkontroll. Om konflikt upptäcks under uppdragets gång kan byrån tvingas frånträda.`,
    },
    {
        title: "Behandling av personuppgifter",
        body: `Personuppgifter behandlas i enlighet med byråns integritetspolicy. Personuppgiftsansvarig är ${FIRM.dpo.name}. Mer information finns på bmjuridik.se/integritetspolicy.`,
    },
    {
        title: "Ansvarsbegränsning",
        body: `Byråns ansvar för skada till följd av fel eller försummelse är begränsat till ett belopp motsvarande tre gånger det arvode som debiterats för det specifika uppdraget, dock alltid minst 500 000 kronor och högst 10 miljoner kronor. Byrån ansvarar inte för indirekt skada eller utebliven vinst. Ansvarsbegränsningen gäller inte vid uppsåt eller grov vårdslöshet.`,
    },
    {
        title: "Penningtvätt",
        body: `Byrån är skyldig att genomföra kundkännedomsåtgärder i enlighet med lagen (2017:630) om åtgärder mot penningtvätt och finansiering av terrorism. Klienten är införstådd med att byrån i vissa fall är skyldig att rapportera misstänkta transaktioner till Finanspolisen.`,
    },
    {
        title: "Immateriella rättigheter",
        body: `Alla immateriella rättigheter till arbetsresultat som tas fram inom ramen för uppdraget tillkommer byrån, men klienten har en oinskränkt rätt att använda resultatet för det ändamål som uppdraget avsett.`,
    },
    {
        title: "Uppsägning",
        body: `Klienten kan när som helst säga upp uppdraget. Byrån kan frånträda uppdraget om förtroende brister, om arvode inte betalas, vid intressekonflikt eller när uppdraget i övrigt inte rimligen kan fortgå.`,
    },
    {
        title: "Tvist",
        body: `Tvist med anledning av dessa villkor eller ett uppdrag ska i första hand lösas genom förhandling och, om detta inte går, genom svensk allmän domstol med Stockholms tingsrätt som första instans. Svensk lag ska tillämpas.

För konsumenter gäller att tvist kan prövas av Allmänna reklamationsnämnden (arn.se) och via EU-kommissionens plattform för tvistlösning online (ec.europa.eu/consumers/odr).`,
    },
];

export default function AllmannaVillkor() {
    useSEO({
        title: "Allmänna villkor",
        description:
            "Våra allmänna villkor för uppdrag — arvode, rättsskydd, sekretess och ansvarsbegränsning.",
    });

    return (
        <div data-testid="page-allmanna-villkor">
            <PageHeader
                kicker="Allmänna villkor"
                title="Villkor för"
                accent="våra uppdrag."
                description="Dessa villkor gäller tillsammans med det individuella uppdragsbrevet mellan BM Juridik och dig som klient."
            />
            <section className="py-20 bg-[#0A0A0B]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="space-y-10">
                        {SECTIONS.map((s, i) => (
                            <motion.section
                                key={s.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.03 }}
                                data-testid={`terms-section-${i}`}
                            >
                                <p className="overline">
                                    § {String(i + 1).padStart(2, "0")}
                                </p>
                                <h2 className="mt-2 font-serif text-3xl text-[#F4F4F5] tracking-tight">
                                    {s.title}
                                </h2>
                                <div className="mt-4 text-[#A1A1AA] leading-relaxed text-[15px] whitespace-pre-wrap">
                                    {s.body}
                                </div>
                            </motion.section>
                        ))}
                    </div>
                    <p className="mt-16 text-xs text-[#A1A1AA]/80">
                        Senast uppdaterad: {new Date().toLocaleDateString("sv-SE", { year: "numeric", month: "long" })}
                    </p>
                </div>
            </section>
        </div>
    );
}
